// If a component throws an exception, we rebuild without it.
import { Kws, Suspension, chainOrSuspend, pyCallOrSuspend, pyDict, pyObject, tryCatchOrSuspend } from "@Sk";
import { DesignLinkV3 } from "@designer/components/DesignLinkV3";
import { DesignYouTubeVideoV3 } from "@designer/components/DesignYouTubeVideoV3";
import { Component, ComponentConstructor, runOnNextInstantiation } from "@runtime/components/Component";
import { getAndCheckNextCreationStack, setNextCreationStack } from "@runtime/runner/component-creation";
import {
    FormInstantiationFlags,
    InstantiationContext,
    InstantiatorFunction,
    ResolvedForm,
    getFormClassObject,
    setInstantiationHooks,
} from "@runtime/runner/instantiation";
import { PyModMap, anvilMod, reportError } from "@runtime/runner/py-util";
import { warn } from "@runtime/runner/warnings";
import { getFormIndexEntry, isForm } from "./app-content";
import { DesignCanvasV3 } from "./components/DesignCanvasV3";
import { DesignColumnPanelV3 } from "./components/DesignColumnPanelV3";
import { DesignDataGridV3 } from "./components/DesignDataGridV3";
import { DesignDataRowPanelV3 } from "./components/DesignDataRowPanelV3";
import { DesignFlowPanelV3 } from "./components/DesignFlowPanelV3";
import { DesignGridPanelV3 } from "./components/DesignGridPanelV3";
import { DesignHtmlTemplateV3 } from "./components/DesignHtmlTemplateV3";
import { DesignImageV3 } from "./components/DesignImageV3";
import { DesignLinearPanelV3 } from "./components/DesignLinearPanelV3";
import { DesignRepeatingPanelV3 } from "./components/DesignRepeatingPanelV3";
import { DesignRichTextV3 } from "./components/DesignRichTextV3";
import { DesignSpacerV3 } from "./components/DesignSpacerV3";
import { DesignXYPanelV3 } from "./components/DesignXYPanelV3";
import { PathElement, editState, onInstantiatedEditingForm, refreshEditingForm } from "./edit-state";
import { equalArray } from "./helpers";
import { BASE_MODULES } from "./python-environment";
import { YamlCarrierState, createYamlCarrier } from "./yaml-carriers";

type DesignerPath = PathElement[];

// Custom properties in the designer only - use ts declaration-merging
declare module "../../../../runtime/client/js/components/Component" {
    export interface Component extends pyObject {
        anvil$designerPath?: DesignerPath;
        anvil$yamlCarrier?: YamlCarrierState;
        $d: pyDict; // TODO should we just add this to the base class - then we don't need subclasses to instantiate it
    }
}

let failedComponents: Set<string> = new Set();

const catchComponentFailure = <R>(
    componentId: string,
    rebuildOnError: boolean,
    fn: () => R | Suspension,
    onException?: (exc: any) => R | Suspension
): R | Suspension =>
    tryCatchOrSuspend(fn, (exc) => {
        if (rebuildOnError) {
            const { topLevelFormName, editPath } = editState;
            window.setTimeout(() => refreshEditingForm());
        }
        if (failedComponents.has(componentId)) {
            throw exc;
        }
        failedComponents.add(componentId);
        if (onException) {
            return onException(exc);
        } else {
            throw exc;
        }
    });

interface DesignerFormInstantiationFlags extends FormInstantiationFlags {
    isTopLevel?: true;
}

const warnedAboutModuleForms = new Set<string>();

function instantiateFully(
    formName: string,
    depId: string | null,
    isOnEditPath: boolean,
    flags?: DesignerFormInstantiationFlags
) {
    // Simple policy:
    // Layouts are always instantiated.
    // Hand-crafted (JS+Python) components are always instantiated.
    // Otherwise, we instantiate iff instantiating a component hasn't failed.

    const formEntry = getFormIndexEntry(depId, formName);

    if (flags?.asLayout || !formEntry) {
        return true;
    }

    const stringId = depId ? `${depId}:${formName}` : formName;

    if (isOnEditPath || flags?.preferLiveDesign === false || failedComponents.has(stringId)) {
        return false;
    }

    if (!formEntry.isPackage) {
        if (!warnedAboutModuleForms.has(stringId)) {
            warn(
                `Displaying form "${formName}" as a skeleton in designer because it is a module form. Convert it to a package to interact with it live in the designer.`
            );
            warnedAboutModuleForms.add(stringId);
        }
        return false;
    }

    return true;
}

const setPathOnNextInstantiation = (path: DesignerPath) => {
    runOnNextInstantiation((component) => {
        component.anvil$designerPath = path;
    });
};

const addPathOnNextInstantiation = (requestingComponent: Component | undefined, pathStep?: string | number) => {
    const designerPath = requestingComponent?.anvil$designerPath;
    if (designerPath && pathStep !== undefined) {
        setPathOnNextInstantiation([...designerPath, pathStep]);
    }
};

export function getNamedFormInstantiator(
    formSpec: ResolvedForm,
    requestingComponent?: Component,
    flags?: DesignerFormInstantiationFlags
): Suspension | InstantiatorFunction {
    const { qualifiedClassName, formName, depId, logicalDepId } = formSpec;
    let errorFromFullInstantiator: any;
    const basePath = requestingComponent?.anvil$designerPath;

    const ifn = (kwargs?: Kws, pathStep?: string | number) => {
        const yamlStack = getAndCheckNextCreationStack(formName, depId);
        setNextCreationStack(yamlStack);

        const instantiateYamlCarrier = (isActive: boolean, kws?: Kws) =>
            //console.log("Instantiate form", fullFormName, "topLevel", isTopLevel, "basePath", basePath, "pathStep", pathStep);
            // Is this the active carrier?
            isActive
                ? chainOrSuspend(
                      createYamlCarrier(formName, null, yamlStack, { withError: false, active: true }),
                      (instance: Component) => {
                          onInstantiatedEditingForm(formName, instance);
                          return instance;
                      }
                  )
                : createYamlCarrier(formName, depId, yamlStack, {
                      withError: !!errorFromFullInstantiator,
                      active: false,
                      kws,
                  });

        const maybeInstantiateForError = (err: Error, newPath: PathElement[] | undefined, kwargs?: Kws) => {
            if (isForm(depId, formName)) {
                errorFromFullInstantiator = err;
                reportError(err);
                if (newPath) {
                    setPathOnNextInstantiation(newPath);
                }
                setNextCreationStack(yamlStack);
                return instantiateYamlCarrier(false, kwargs);
            } else {
                throw err;
            }
        };

        const newPath = flags?.isTopLevel ? [] : (basePath && pathStep && [...basePath, pathStep]) || undefined;
        if (newPath) {
            setPathOnNextInstantiation(newPath);
        }
        const isOnEditPath = newPath && equalArray(newPath, editState.editPath.slice(0, newPath.length));
        if (isOnEditPath) {
            editState.breadcrumb.push({
                className: formName, // Or fullFormName, as above.
                editPath: newPath,
            });
        }
        const isEditingForm = isOnEditPath && newPath.length === editState.editPath.length;
        //console.log("Instantiating", formSpec, "with path", newPath, "on editing path", editState.editPath, ". On path?", isOnEditPath, "; editing?", isEditingForm);
        if (!isEditingForm && instantiateFully(formName, depId, !!isOnEditPath, flags)) {
            return catchComponentFailure(
                qualifiedClassName,
                false,
                () =>
                    chainOrSuspend(getFormClassObject(formSpec), (constructor) => {
                        // console.log("Got constructor:", constructor);
                        if (constructor === undefined) {
                            // TODO - throw a proper Error here or return mkInvalidComponent?
                            throw new Sk.builtin.Exception(`No such form '${formName}'`);
                        }
                        return catchComponentFailure(
                            qualifiedClassName,
                            false,
                            () =>
                                chainOrSuspend(pyCallOrSuspend(constructor, [], kwargs), (pyComponent) => {
                                    // console.log("Constructed", pyComponent);
                                    const depPrefix = logicalDepId ? logicalDepId + ":" : "";
                                    editState.componentTypes.set(pyComponent, `form:${depPrefix}${formName}`);
                                    pyComponent.anvil$hooks.domElement?.setAttribute("anvil-design-form-component", "");
                                    return pyComponent;
                                }),
                            (err) => maybeInstantiateForError(err, newPath, kwargs)
                        );
                    }),
                (err) => maybeInstantiateForError(err, newPath, kwargs)
            );
        } else {
            return instantiateYamlCarrier(!!isEditingForm, kwargs);
        }
    };

    ifn.anvil$instantiatorForForm = formSpec;

    return ifn;
}

export const COMPONENT_OVERRIDES: { [name: string]: ComponentConstructor } = {
    RepeatingPanel: DesignRepeatingPanelV3,
    LinearPanel: DesignLinearPanelV3,
    Spacer: DesignSpacerV3,
    ColumnPanel: DesignColumnPanelV3,
    HtmlPanel: BASE_MODULES["anvil"]["DesignHtmlTemplate"],
    HtmlTemplate: DesignHtmlTemplateV3,
    Label: BASE_MODULES["anvil"]["DesignLabel"],
    TextBox: BASE_MODULES["anvil"]["DesignTextBox"],
    TextArea: BASE_MODULES["anvil"]["DesignTextArea"],
    Link: DesignLinkV3,
    Image: DesignImageV3,
    Canvas: DesignCanvasV3,
    FlowPanel: DesignFlowPanelV3,
    GridPanel: DesignGridPanelV3,
    RichText: DesignRichTextV3,
    XYPanel: DesignXYPanelV3,
    DataGrid: DesignDataGridV3,
    DataRowPanel: DesignDataRowPanelV3,
    YouTubeVideo: DesignYouTubeVideoV3,
};

export function getAnvilComponentClass(anvilModule: PyModMap, componentType: string): ComponentConstructor {
    return COMPONENT_OVERRIDES[componentType] || anvilModule[componentType];
}

export function findDesignerComponentClass(anvilModule: PyModMap, runnerClass: ComponentConstructor) {
    for (let [runnerName, designerClass] of Object.entries(COMPONENT_OVERRIDES)) {
        if (runnerClass === anvilModule[runnerName]) {
            return designerClass;
        }
    }
    return runnerClass;
}

export function getAnvilComponentInstantiator(context: InstantiationContext, componentType: string) {
    // TODO policy decision: Do we use the classic Design* components as is, or do we make v3-specific design versions?
    // TODO only do this replacement when the requestingComponent is a YAML carrier
    const pyComponentConstructor = COMPONENT_OVERRIDES[componentType] || anvilMod[componentType];
    return (kwargs?: Kws, pathStep?: string | number) => {
        addPathOnNextInstantiation(context.requestingComponent, pathStep);
        return chainOrSuspend(pyCallOrSuspend(pyComponentConstructor, [], kwargs), (pyComponent) => {
            editState.componentTypes.set(pyComponent, componentType);
            return pyComponent;
        });
    };
}

setInstantiationHooks({ getNamedFormInstantiator, getAnvilComponentClass, getAnvilComponentInstantiator });
